/**
 * Profile360 API Service
 * Centralized service for handling all API communications
 */

import profile360Config from '../config/profile360Config';

/**
 * API Service class for Profile360
 */
class Profile360ApiService {
  constructor() {
    this.baseUrl = profile360Config.get('api.endpoints.base');
    this.defaultHeaders = {
      'Content-Type': 'application/json',
    };
  }

  /**
   * Set authentication token
   * @param {string} token - JWT token
   */
  setAuthToken(token) {
    this.authToken = token;
  }

  /**
   * Set tenant name for multi-tenant requests
   * @param {string} tenantName - Tenant identifier
   */
  setTenant(tenantName) {
    this.tenantName = tenantName;
  }

  /**
   * Refresh configuration (useful for development)
   */
  refreshConfig() {
    this.baseUrl = profile360Config.get('api.endpoints.base');
  }

  /**
   * Get request headers with auth and tenant info
   * @param {Object} additionalHeaders - Additional headers to include
   * @returns {Object} Complete headers object
   */
  getHeaders(additionalHeaders = {}) {
    const headers = { ...this.defaultHeaders, ...additionalHeaders };

    if (this.authToken) {
      headers.Authorization = `Bearer ${this.authToken}`;
    }

    if (this.tenantName) {
      headers['X-Tenant-Name'] = this.tenantName;
    }

    return headers;
  }

  /**
   * Make HTTP request with error handling
   * @param {string} method - HTTP method
   * @param {string} endpoint - API endpoint
   * @param {Object} options - Request options
   * @returns {Promise<Object>} API response
   */
  async makeRequest(method, endpoint, options = {}) {
    const { params = {}, body = null, headers = {} } = options;

    try {
      // Build URL with parameters
      let url = `${this.baseUrl}${endpoint}`;

      // Separate path parameters from query parameters
      const pathParams = {};
      const queryParams = {};

      Object.keys(params).forEach(key => {
        if (endpoint.includes(`{${key}}`)) {
          // This is a path parameter
          pathParams[key] = params[key];
        } else {
          // This is a query parameter
          queryParams[key] = params[key];
        }
      });

      // Replace path parameters
      Object.keys(pathParams).forEach(key => {
        url = url.replace(`{${key}}`, encodeURIComponent(pathParams[key]));
      });

      // Add query parameters for GET requests
      if (method.toUpperCase() === 'GET' && Object.keys(queryParams).length > 0) {
        const searchParams = new URLSearchParams();
        Object.keys(queryParams).forEach(key => {
          if (queryParams[key] !== null && queryParams[key] !== undefined) {
            searchParams.append(key, queryParams[key]);
          }
        });
        url += `?${searchParams.toString()}`;
      }

      const requestConfig = {
        method: method.toUpperCase(),
        headers: this.getHeaders(headers),
      };

      // Add body for POST/PUT requests
      if (body && (method.toUpperCase() === 'POST' || method.toUpperCase() === 'PUT')) {
        requestConfig.body = JSON.stringify(body);
      }

      const response = await fetch(url, requestConfig);

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`API request failed: ${response.status} ${response.statusText} - ${errorText}`);
      }

      const data = await response.json();

      return {
        success: true,
        data,
        status: response.status,
        statusText: response.statusText
      };

    } catch (error) {
      return {
        success: false,
        error: error.message,
        status: error.status || 500
      };
    }
  }

  /**
   * Execute GET request
   * @param {string} endpoint - API endpoint
   * @param {Object} options - Request options
   * @returns {Promise<Object>} API response
   */
  async get(endpoint, options = {}) {
    return this.makeRequest('GET', endpoint, options);
  }

  /**
   * Execute POST request
   * @param {string} endpoint - API endpoint
   * @param {Object} body - Request body
   * @param {Object} options - Request options
   * @returns {Promise<Object>} API response
   */
  async post(endpoint, body = {}, options = {}) {
    return this.makeRequest('POST', endpoint, { ...options, body });
  }

  /**
   * Execute PUT request
   * @param {string} endpoint - API endpoint
   * @param {Object} body - Request body
   * @param {Object} options - Request options
   * @returns {Promise<Object>} API response
   */
  async put(endpoint, body = {}, options = {}) {
    return this.makeRequest('PUT', endpoint, { ...options, body });
  }

  /**
   * Execute DELETE request
   * @param {string} endpoint - API endpoint
   * @param {Object} options - Request options
   * @returns {Promise<Object>} API response
   */
  async delete(endpoint, options = {}) {
    return this.makeRequest('DELETE', endpoint, options);
  }

  // ===== Profile360 Specific Methods =====

  /**
   * Get dormant users
   * @returns {Promise<Object>} Dormant users data
   */
  async getDormantUsers() {
    return this.get('/api/dormant-users');
  }

  /**
   * Get stale apps for user
   * @param {string} userEmail - User email
   * @param {number} days - Number of days to consider as stale threshold (default: 30)
   * @returns {Promise<Object>} Stale apps data
   */
  async getStaleAppsForUser(userEmail, days = 30) {
    const params = { days };
    return this.get(`/api/users/${encodeURIComponent(userEmail)}/stale-apps`, { params });
  }

  /**
   * Get user applications
   * @param {string} userId - User ID
   * @returns {Promise<Object>} User applications data
   */
  async getUserApplications(userId) {
    return this.get('/api/users/{user_id}/applications', {
      params: { user_id: userId }
    });
  }

  /**
   * Get user knowledge graph
   * @param {string} userId - User ID
   * @returns {Promise<Object>} Knowledge graph data
   */
  async getUserKnowledgeGraph(userId) {
    return this.get('/api/knowledge-graph/user/{user_id}', {
      params: { user_id: userId }
    });
  }

  /**
   * Execute natural language query
   * @param {string} query - Natural language query
   * @returns {Promise<Object>} Query results
   */
  async executeNaturalLanguageQuery(query) {
    return this.post('/api/knowledge-graph/natural-language', { query });
  }

  /**
   * Execute Cypher query
   * @param {string} query - Cypher query
   * @returns {Promise<Object>} Query results
   */
  async executeCypherQuery(query) {
    return this.post('/api/knowledge-graph/query', { query });
  }

  /**
   * Get sample queries
   * @returns {Promise<Object>} Sample queries
   */
  async getSampleQueries() {
    return this.get('/api/knowledge-graph/sample-queries');
  }

  /**
   * Get database schema
   * @returns {Promise<Object>} Database schema
   */
  async getDatabaseSchema() {
    return this.get('/api/knowledge-graph/schema');
  }

  /**
   * Get database statistics
   * @returns {Promise<Object>} Database statistics
   */
  async getDatabaseStats() {
    return this.get('/api/knowledge-graph/stats');
  }

  /**
   * Apply graph filters
   * @param {Object} filters - Filter parameters
   * @returns {Promise<Object>} Filtered results
   */
  async applyGraphFilters(filters) {
    return this.post('/api/knowledge-graph/filter', filters);
  }

  /**
   * Get knowledge graph health
   * @returns {Promise<Object>} Health status
   */
  async getKnowledgeGraphHealth() {
    return this.get('/api/knowledge-graph/health');
  }

  /**
   * Get all users
   * @returns {Promise<Object>} Users list
   */
  async getAllUsers() {
    return this.get('/users/');
  }

  /**
   * Get specific user
   * @param {string} userId - User ID
   * @returns {Promise<Object>} User data
   */
  async getUser(userId) {
    return this.get('/users/{user_id}', {
      params: { user_id: userId }
    });
  }

  /**
   * Get current user
   * @returns {Promise<Object>} Current user data
   */
  async getCurrentUser() {
    return this.get('/users/me');
  }

  /**
   * Get user access summary
   * @param {string} email - User email
   * @returns {Promise<Object>} User access summary data
   */
  async getUserAccessSummary(email) {
    return this.post('/api/knowledge-graph/user_access_summary', { email });
  }

  /**
   * Get all applications
   * @returns {Promise<Object>} Applications list
   */
  async getAllApplications() {
    return this.get('/api/applications');
  }

  /**
   * Get all tenants
   * @returns {Promise<Object>} Tenants list
   */
  async getAllTenants() {
    return this.get('/api/tenants');
  }

  // ===== Analytics Methods =====

  /**
   * Check analytics service health
   * @returns {Promise<Object>} Analytics health status
   */
  async getAnalyticsHealth() {
    return this.get('/api/analytics/health');
  }

  /**
   * Get risk scores for users
   * @param {Object} options - Options for risk scoring
   * @param {Array<string>} options.userIds - Optional list of user IDs
   * @param {number} options.threshold - Risk threshold (default: 0.7)
   * @returns {Promise<Object>} Risk scoring results
   */
  async getRiskScores(options = {}) {
    const { userIds = null, threshold = 0.7 } = options;
    return this.post('/api/analytics/risk-scores', { user_ids: userIds, threshold });
  }

  /**
   * Get user insights (risk scores for specific user by email)
   * @param {string} email - User email address
   * @returns {Promise<Object>} User insights data
   */
  async getUserInsights(email) {
    return this.post('/api/analytics/user-insights', { email });
  }

  /**
   * Detect anomalies in user behavior
   * @param {Object} options - Options for anomaly detection
   * @param {Array<string>} options.userIds - Optional list of user IDs
   * @returns {Promise<Object>} Anomaly detection results
   */
  async detectAnomalies(options = {}) {
    const { userIds = null } = options;
    return this.post('/api/analytics/anomalies', { user_ids: userIds });
  }

  /**
   * Generate recommendations
   * @param {Object} options - Options for recommendations
   * @param {Array<string>} options.userIds - Optional list of user IDs
   * @returns {Promise<Object>} Recommendations results
   */
  async getRecommendations(options = {}) {
    const { userIds = null } = options;
    return this.post('/api/analytics/recommendations', { user_ids: userIds });
  }

  /**
   * Run graph analytics
   * @returns {Promise<Object>} Graph analytics results (PageRank, communities, centrality)
   */
  async getGraphAnalytics() {
    return this.post('/api/analytics/graph-analytics');
  }

  /**
   * Run all analytics operations
   * @param {Object} options - Options for analytics
   * @param {number} options.threshold - Risk threshold (default: 0.7)
   * @returns {Promise<Object>} Combined analytics results
   */
  async getAllAnalytics(options = {}) {
    const { threshold = 0.7 } = options;
    return this.post('/api/analytics/all', { threshold });
  }

  /**
   * Execute specific analytics operation
   * @param {Object} options - Analytics options
   * @param {string} options.operation - Operation type ('risk_scoring', 'anomaly_detection', 'recommendations', 'graph_analytics', 'all')
   * @param {Array<string>} options.userIds - Optional list of user IDs
   * @param {number} options.threshold - Risk threshold (default: 0.7)
   * @returns {Promise<Object>} Analytics results
   */
  async executeAnalytics(options = {}) {
    const { operation = 'all', userIds = null, threshold = 0.7 } = options;
    return this.post('/api/analytics/execute', { operation, user_ids: userIds, threshold });
  }

  // ===== AI-Powered Dormant User Insights =====

  /**
   * Get AI-generated dormant user insights
   * @param {Object} options - Query options
   * @param {string} options.classification - Filter by classification (DORMANT, POTENTIALLY_DORMANT, ACTIVE)
   * @param {number} options.minRiskScore - Minimum risk score (1-10)
   * @param {number} options.page - Page number (default: 1)
   * @param {number} options.pageSize - Items per page (default: 20)
   * @returns {Promise<Object>} AI insights data
   */
  async getAIDormantInsights(options = {}) {
    const { classification = null, minRiskScore = null, page = 1, pageSize = 20 } = options;
    const params = {};
    
    if (classification) params.classification = classification;
    if (minRiskScore) params.min_risk_score = minRiskScore;
    params.limit = pageSize;
    params.offset = (page - 1) * pageSize;

    return this.get('/api/ai-insights', { params });
  }

  /**
   * Get AI insight for a specific user
   * @param {string} userEmail - User email address
   * @returns {Promise<Object>} User AI insight data
   */
  async getUserAIInsight(userEmail) {
    return this.get(`/api/users/${encodeURIComponent(userEmail)}/ai-insight`);
  }

  /**
   * Trigger AI analysis (Admin only)
   * @param {Object} options - Analysis options
   * @param {number} options.daysInactive - Days inactive threshold (default: 30)
   * @param {number} options.batchSize - Batch size (default: 50)
   * @param {string} options.environment - Environment (dev/preprod/prod)
   * @param {boolean} options.asyncInvoke - Use async invocation (default: false)
   * @returns {Promise<Object>} Analysis trigger response
   */
  async triggerAIAnalysis(options = {}) {
    const { daysInactive = 30, batchSize = 50, environment = 'dev', asyncInvoke = false } = options;
    const params = {
      days_inactive: daysInactive,
      batch_size: batchSize,
      environment,
      async_invoke: asyncInvoke
    };
    return this.post('/api/trigger-analysis', {}, { params });
  }

  /**
   * Get AI analysis history
   * @param {number} limit - Maximum number of results (default: 10)
   * @returns {Promise<Object>} Analysis history data
   */
  async getAnalysisHistory(limit = 10) {
    return this.get('/api/analysis-history', { params: { limit } });
  }

  /**
   * Get risk distribution across classifications
   * @returns {Promise<Object>} Risk distribution data
   */
  async getRiskDistribution() {
    return this.get('/api/risk-distribution');
  }

  /**
   * Get high-risk dormant users
   * @param {Object} options - Query options
   * @param {number} options.minRiskScore - Minimum risk score (default: 8)
   * @param {number} options.limit - Maximum number of users (default: 20)
   * @returns {Promise<Object>} High-risk users data
   */
  async getHighRiskUsers(options = {}) {
    const { minRiskScore = 8, limit = 20 } = options;
    return this.get('/api/high-risk-users', {
      params: { min_risk_score: minRiskScore, limit }
    });
  }

  /**
   * Get comprehensive dormant analytics for a specific user
   * @param {number} userId - User ID
   * @param {number} daysThreshold - Days threshold for dormancy calculation (default: 90)
   * @returns {Promise<Object>} User dormant analytics data
   */
  async getUserDormantAnalytics(userId, daysThreshold = 90) {
    return this.get(`/users/{userId}/dormant-analytics`, {
      params: {
        userId: userId,
        days_threshold: daysThreshold
      }
    });
  }

  // ===== Privilege Creep Detection =====

  /**
   * Get privilege creep detection summary
   * @returns {Promise<Object>} Privilege creep summary with risk distribution
   */
  async getPrivilegeCreepSummary() {
    return this.get('/api/privilege-creep/summary');
  }

  /**
   * Get privilege creep risk distribution heatmap data
   * @returns {Promise<Object>} Heatmap data for privilege creep risk
   */
  async getPrivilegeCreepHeatmap() {
    return this.get('/api/privilege-creep/heatmap');
  }

  /**
   * Get privilege creep trends over time
   * @param {number} days - Number of days to look back (default: 30)
   * @returns {Promise<Object>} Trend data for privilege creep
   */
  async getPrivilegeCreepTrends(days = 30) {
    return this.get('/api/privilege-creep/trends', {
      params: { days }
    });
  }

  /**
   * Get peer comparison data for privilege creep
   * @returns {Promise<Object>} Peer comparison data
   */
  async getPrivilegeCreepPeerComparison() {
    return this.get('/api/privilege-creep/peer-comparison');
  }

  /**
   * Get filtered list of users with privilege creep insights
   * @param {Object} options - Filter options
   * @param {string} options.department - Filter by department
   * @param {string} options.riskLevel - Filter by risk level
   * @param {string} options.classification - Filter by classification
   * @param {number} options.minRiskScore - Minimum risk score
   * @param {number} options.maxRiskScore - Maximum risk score
   * @param {number} options.limit - Number of results (default: 50)
   * @param {number} options.offset - Offset for pagination (default: 0)
   * @returns {Promise<Object>} Filtered user list with privilege creep data
   */
  async getPrivilegeCreepUsers(options = {}) {
    const {
      department = null,
      riskLevel = null,
      classification = null,
      minRiskScore = null,
      maxRiskScore = null,
      limit = 50,
      offset = 0
    } = options;

    return this.get('/api/privilege-creep/users', {
      params: {
        department,
        risk_level: riskLevel,
        classification,
        min_risk_score: minRiskScore,
        max_risk_score: maxRiskScore,
        limit,
        offset
      }
    });
  }

  // ===== Excessive Access Detection =====

  /**
   * Get excessive access detection summary
   * @returns {Promise<Object>} Excessive access summary with classification counts
   */
  async getExcessiveAccessSummary() {
    return this.get('/api/excessive-access/summary');
  }

  /**
   * Get excessive access risk distribution by classification
   * @returns {Promise<Object>} Distribution data for excessive access risk
   */
  async getExcessiveAccessRiskDistribution() {
    return this.get('/api/excessive-access/risk-distribution');
  }

  /**
   * Get excessive access z-score heatmap data
   * @returns {Promise<Object>} Heatmap data for z-scores by department/cohort
   */
  async getExcessiveAccessZScoreHeatmap() {
    return this.get('/api/excessive-access/z-score-heatmap');
  }

  /**
   * Get cohort comparison statistics for excessive access
   * @returns {Promise<Object>} Cohort comparison data
   */
  async getExcessiveAccessCohortComparison() {
    return this.get('/api/excessive-access/cohort-comparison');
  }

  /**
   * Get excessive access trends over time
   * @param {number} days - Number of days to look back (default: 30)
   * @returns {Promise<Object>} Trend data for excessive access
   */
  async getExcessiveAccessTrends(days = 30) {
    return this.get('/api/excessive-access/trends', {
      params: { days }
    });
  }

  /**
   * Get filtered list of users with excessive access insights
   * @param {Object} options - Filter options
   * @param {string} options.department - Filter by department
   * @param {string} options.classification - Filter by classification (CRITICAL_EXCESS, HIGH_EXCESS, MODERATE_EXCESS, ACCEPTABLE)
   * @param {number} options.minRiskScore - Minimum risk score
   * @param {number} options.maxRiskScore - Maximum risk score
   * @param {number} options.minZScore - Minimum z-score
   * @param {number} options.cohortId - Filter by cohort ID
   * @param {string} options.sortBy - Sort field (default: 'risk_score')
   * @param {string} options.sortOrder - Sort order 'asc' or 'desc' (default: 'desc')
   * @param {number} options.limit - Number of results (default: 50)
   * @param {number} options.offset - Offset for pagination (default: 0)
   * @returns {Promise<Object>} Filtered user list with excessive access insights
   */
  async getExcessiveAccessInsights(options = {}) {
    const {
      department = null,
      classification = null,
      minRiskScore = null,
      maxRiskScore = null,
      minZScore = null,
      cohortId = null,
      sortBy = 'risk_score',
      sortOrder = 'desc',
      limit = 50,
      offset = 0
    } = options;

    return this.get('/api/excessive-access/insights', {
      params: {
        department,
        classification,
        min_risk_score: minRiskScore,
        max_risk_score: maxRiskScore,
        min_z_score: minZScore,
        cohort_id: cohortId,
        sort_by: sortBy,
        sort_order: sortOrder,
        limit,
        offset
      }
    });
  }

  /**
   * Get detailed excessive access insight for a specific user
   * @param {string} userId - User ID
   * @returns {Promise<Object>} Detailed user excessive access insight
   */
  async getExcessiveAccessUserDetail(userId) {
    return this.get(`/api/excessive-access/users/{user_id}`, {
      params: { user_id: userId }
    });
  }

  // ============================================================================
  // Suspicious Activity Detection API Methods
  // ============================================================================

  /**
   * Get suspicious activity detection summary
   * @returns {Promise<Object>} Suspicious activity summary with classification counts
   */
  async getSuspiciousActivitySummary() {
    return this.get('/api/suspicious-activity/summary');
  }

  /**
   * Get filtered list of users with suspicious activity insights
   * @param {Object} options - Filter options
   * @param {string} options.classification - Filter by classification (HIGH_RISK, MEDIUM_RISK, LOW_RISK, NO_THREAT)
   * @param {number} options.minSuspicionScore - Minimum suspicion score (0-100)
   * @param {number} options.skip - Offset for pagination (default: 0)
   * @param {number} options.limit - Number of results (default: 50)
   * @returns {Promise<Object>} Filtered user list with suspicious activity insights
   */
  async getSuspiciousActivityUsers(options = {}) {
    const {
      classification = null,
      minSuspicionScore = null,
      skip = 0,
      limit = 50
    } = options;

    return this.get('/api/suspicious-activity/users', {
      params: {
        classification,
        min_suspicion_score: minSuspicionScore,
        skip,
        limit
      }
    });
  }

  /**
   * Get detailed suspicious activity insight for a specific user
   * @param {string} userId - User ID
   * @returns {Promise<Object>} Detailed user suspicious activity insight
   */
  async getSuspiciousActivityUserDetail(userId) {
    return this.get(`/api/suspicious-activity/users/{user_id}`, {
      params: { user_id: userId }
    });
  }

  /**
   * Trigger manual suspicious activity analysis
   * @param {Object} options - Trigger options
   * @param {number} options.lookbackDays - Days to look back (default: 30)
   * @param {number} options.batchSize - Batch size (default: 50)
   * @param {string} options.model - AI model to use ('haiku' or 'sonnet', default: 'haiku')
   * @returns {Promise<Object>} Trigger response with execution ID
   */
  async triggerSuspiciousActivityAnalysis(options = {}) {
    const {
      lookbackDays = 30,
      batchSize = 50,
      model = 'haiku'
    } = options;

    return this.post('/api/suspicious-activity/trigger', {
      body: {
        lookback_days: lookbackDays,
        batch_size: batchSize,
        model
      }
    });
  }

  // ============================================================================
  // Trust Degradation Detection API Methods (Pillar 3)
  // ============================================================================

  /**
   * Get trust degradation summary
   * @returns {Promise<Object>} Trust degradation summary with level counts and trends
   */
  async getTrustDegradationSummary() {
    return this.get('/api/trust-degradation/summary');
  }

  /**
   * Get filtered list of trust degradation scores
   * @param {Object} options - Filter options
   * @param {string} options.trustLevel - Filter by trust level (CRITICAL, DEGRADED, FAIR, GOOD, EXCELLENT)
   * @param {string} options.trustTrend - Filter by trend (DEGRADING, STABLE, IMPROVING)
   * @param {number} options.minScore - Minimum trust score (0-100)
   * @param {number} options.maxScore - Maximum trust score (0-100)
   * @param {number} options.limit - Number of results (default: 50)
   * @param {number} options.offset - Offset for pagination (default: 0)
   * @returns {Promise<Object>} Filtered user list with trust scores
   */
  async getTrustDegradationScores(options = {}) {
    const {
      trustLevel = null,
      trustTrend = null,
      minScore = null,
      maxScore = null,
      limit = 50,
      offset = 0
    } = options;

    return this.get('/api/trust-degradation/scores', {
      params: {
        trust_level: trustLevel,
        trust_trend: trustTrend,
        min_score: minScore,
        max_score: maxScore,
        limit,
        offset
      }
    });
  }

  /**
   * Get detailed trust score for a specific user
   * @param {string} userId - User ID
   * @returns {Promise<Object>} Detailed user trust score with all components and history
   */
  async getTrustDegradationUserDetail(userId) {
    return this.get(`/api/trust-degradation/scores/{user_id}`, {
      params: { user_id: userId }
    });
  }

  /**
   * Get users with rapidly degrading trust (high-risk)
   * @param {Object} options - Filter options
   * @param {number} options.minVelocity - Minimum access changes in 7 days (default: 5)
   * @param {number} options.limit - Number of results (default: 50)
   * @returns {Promise<Object>} High-risk users with degrading trust
   */
  async getTrustDegradationHighRisk(options = {}) {
    const { minVelocity = 5, limit = 50 } = options;

    return this.get('/api/trust-degradation/high-risk', {
      params: {
        min_velocity: minVelocity,
        limit
      }
    });
  }

  /**
   * Get access change velocity statistics
   * @returns {Promise<Object>} Velocity statistics and top users by change rate
   */
  async getAccessVelocityStats() {
    return this.get('/api/trust-degradation/velocity');
  }

  /**
   * Get access change history and velocity for a specific user
   * @param {string} userId - User ID
   * @param {number} days - Lookback period in days (default: 30)
   * @returns {Promise<Object>} User's access change history and velocity metrics
   */
  async getUserAccessVelocity(userId, days = 30) {
    return this.get(`/api/trust-degradation/velocity/{user_id}`, {
      params: { user_id: userId, days }
    });
  }
}

// Create singleton instance
const apiService = new Profile360ApiService();

export default apiService;
